// --------------------------------------------------------------------------
//  Export builder file generators.
//
//  Consumes the resolved section blocks from exportData.js (which already
//  carry real numbers and rows) and renders them into the four download
//  formats. The same block shapes drive the on-screen preview, so a generated
//  file matches what the customer saw while building it.
//
//    - PDF        : jsPDF, drawn section by section with real tables/metrics.
//    - PowerPoint : self-contained HTML deck, one slide per section, opens as
//                   a presentation and exports to PPTX via the OS print path.
//    - Excel      : a genuine .xlsx workbook built with SheetJS, one sheet per
//                   section, each laid out as plain rows-and-columns. Real
//                   binary OOXML, so Excel opens it with no "format/extension
//                   don't match" warning.
//    - CSV        : labelled data blocks, one per section.
//
//  Large tables export the same four ways (CSV / Excel / JSON / PDF) at full
//  row count, separate from the composed document.
//
//  Postgres mapping: none, pure client rendering. The PDF pipeline is the
//  piece a server-side renderer would eventually replace for pixel-perfect
//  charts; the block contract here is what that service would consume.
// --------------------------------------------------------------------------

import jsPDF from 'jspdf';
import * as XLSX from 'xlsx';
import JSZip from 'jszip';
import { escapeCsv, slugify } from './exportData';

// The real OOXML spreadsheet MIME. We hand downloadBlob a Blob already typed
// with this, so the saved .xlsx carries the correct content type and Excel
// opens it without the format/extension warning the old HTML-as-.xls produced.
const PPTX_MIME = 'application/vnd.openxmlformats-officedocument.presentationml.presentation';
const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

// Composed documents cap each table so a board report stays readable; the
// full dataset lives in the Large data tables panel. Notes make the cap
// explicit so a truncated table never reads as "this is everything".
const DOC_TABLE_LIMIT = 20;
const todayLabel = () => new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });

// Documents carry the tenancy's name rather than a hardcoded "Boxphish", so a
// white-labelled or partner tenancy's reports read as their own. `meta` also
// carries the human label of the selected data period for the cover line.
const brandOf = (meta) => (meta && meta.brand) || 'Boxphish';

// One palette for both the PDF and the deck, matching the risk bands the app
// uses on screen (riskScore.js BANDS) so a score reads the same colour in a
// report as it does in the product.
const DOC = {
    ink: [15, 23, 42],
    body: [51, 65, 85],
    muted: [100, 116, 139],
    faint: [148, 163, 184],
    rule: [226, 232, 240],
    hairline: [241, 245, 249],
    zebra: [248, 250, 252],
    teal: [0, 191, 165],
    tealSoft: [204, 242, 237],
};
const BAND_RGB = {
    Low: [102, 187, 106],
    Medium: [255, 202, 40],
    High: [255, 112, 67],
    Critical: [255, 82, 82],
};
const bandRgb = (band) => BAND_RGB[band] || DOC.teal;
const BAND_HEX = { Low: '66BB6A', Medium: 'FFCA28', High: 'FF7043', Critical: 'FF5252' };

// Nice axis bounds for a value series: pad the real range a little and round
// outwards, so a trend that lives between 31 and 40 fills the plot instead of
// hugging the floor of a 0-100 axis.
function niceScale(values, { floorAtZero = false } = {}) {
    const nums = values.map((v) => Number(v) || 0);
    let lo = nums.length ? Math.min(...nums) : 0;
    let hi = nums.length ? Math.max(...nums) : 1;
    if (hi === lo) { hi = lo + 1; }
    const pad = (hi - lo) * 0.15;
    lo = floorAtZero ? 0 : Math.max(0, lo - pad);
    hi = hi + pad;
    const span = hi - lo;
    const step = Math.pow(10, Math.floor(Math.log10(span / 3 || 1)));
    const niceStep = span / 3 <= step ? step : (span / 3 <= step * 2 ? step * 2 : (span / 3 <= step * 5 ? step * 5 : step * 10));
    lo = Math.floor(lo / niceStep) * niceStep;
    hi = Math.ceil(hi / niceStep) * niceStep;
    const ticks = [];
    for (let v = lo; v <= hi + 1e-9; v += niceStep) ticks.push(Math.round(v * 100) / 100);
    return { lo, hi, ticks };
}

// ==========================================================================
//  CSV  (a zip of real per-section CSV files)
// ==========================================================================
// CSV can only hold one flat table, and a composed document holds many
// differently-shaped sections - so the CSV format produces a .zip with one
// valid, parseable CSV per section plus a small report-info file, mirroring
// how the Excel export gives each section its own sheet.
const aoaToCsv = (aoa) => aoa.map((r) => r.map(escapeCsv).join(',')).join('\n');

export function csvFilesModel(title, sections, meta = {}) {
    const info = [
        ['Report', title || 'Report'],
        ['Generated', `${todayLabel()} · ${brandOf(meta)}`],
        ...(meta.periodLabel ? [['Data period', meta.periodLabel]] : []),
        ...(meta.scopeLabel ? [['Filtered to', meta.scopeLabel]] : []),
        ['Sections', sections.length],
    ];
    return [
        { name: '00-report.csv', aoa: info },
        ...sections.map((s, i) => ({ name: `${String(i + 1).padStart(2, '0')}-${slugify(s.label)}.csv`, aoa: blockToAoa(s.block) })),
    ];
}

async function generateCsvZip(title, sections, meta = {}) {
    const zip = new JSZip();
    // UTF-8 BOM so Excel decodes the files correctly when opened directly.
    csvFilesModel(title, sections, meta).forEach((f) => zip.file(f.name, '﻿' + aoaToCsv(f.aoa)));
    return zip.generateAsync({ type: 'blob', compression: 'DEFLATE' });
}

// ==========================================================================
//  Excel  (genuine .xlsx workbook via SheetJS)
// ==========================================================================

// Excel sheet names: max 31 chars, must be non-empty, may not contain : \ / ? * [ ].
function sanitizeSheetName(name) {
    const cleaned = String(name == null ? '' : name).replace(/[:\\/?*[\]]/g, ' ').replace(/\s+/g, ' ').trim();
    return cleaned.slice(0, 31) || 'Sheet';
}

// Size each column to its widest cell so the sheet opens readable rather than
// with every column clamped to the default width.
function autoFitColumns(aoa) {
    const ncol = aoa.reduce((m, r) => Math.max(m, r.length), 0);
    const cols = [];
    for (let c = 0; c < ncol; c++) {
        let widest = 8;
        for (const row of aoa) {
            const v = row[c];
            if (v != null) widest = Math.max(widest, String(v).length + 2);
        }
        cols.push({ wch: Math.min(widest, 60) });
    }
    return cols;
}

// Build a real .xlsx Blob from [{ name, aoa }] sheets. Sheet names are
// sanitised and de-duplicated so book_append_sheet never throws.
function buildWorkbook(sheets) {
    const wb = XLSX.utils.book_new();
    const used = new Set();
    sheets.forEach((s, idx) => {
        const aoa = s.aoa && s.aoa.length ? s.aoa : [['']];
        const ws = XLSX.utils.aoa_to_sheet(aoa);
        ws['!cols'] = autoFitColumns(aoa);
        let name = sanitizeSheetName(s.name || `Sheet ${idx + 1}`);
        let unique = name, n = 2;
        while (used.has(unique.toLowerCase())) { unique = `${name.slice(0, 28)} ${n++}`; }
        used.add(unique.toLowerCase());
        XLSX.utils.book_append_sheet(wb, ws, unique);
    });
    const buf = XLSX.write(wb, { type: 'array', bookType: 'xlsx' });
    return new Blob([buf], { type: XLSX_MIME });
}

// Turn a resolved block into a plain rows-and-columns array of arrays. Values
// stay exactly as the preview shows them (already formatted), so what the
// customer saw while building the export is what lands in the cells.
function blockToAoa(block) {
    if (block.kind === 'gauge') {
        return [['Metric', 'Value'], ['Risk score', block.value], ['Band', block.band], ['Context', block.sub]];
    }
    if (block.kind === 'metrics') {
        return [['Metric', 'Value', 'Detail'], ...block.items.map((m) => [m.label, m.value, m.sub])];
    }
    if (block.kind === 'bars' || block.kind === 'area') {
        return [['Label', block.unit || 'Value'], ...block.items.map((b) => [b.label, b.value])];
    }
    if (block.kind === 'table') {
        return [block.columns, ...block.rows];
    }
    if (block.kind === 'narrative') {
        const aoa = [['Section', 'Detail']];
        block.paragraphs.forEach((p) => {
            aoa.push([p.h || '', p.body]);
            if (p.action) aoa.push(['Action', p.action]);
        });
        return aoa;
    }
    return [['']];
}

// The workbook layout as plain [{ name, aoa }] sheets: a cover sheet listing
// the document contents, then one clean tab per section. Exported so the
// on-screen Excel preview renders exactly what the .xlsx will contain.
export function buildWorkbookModel(title, sections, meta = {}) {
    const cover = {
        name: 'Report',
        aoa: [
            [title || 'Report'],
            [`Generated ${todayLabel()} · ${brandOf(meta)}`],
            ...(meta.periodLabel ? [[`Data period: ${meta.periodLabel}`]] : []),
            ...(meta.scopeLabel ? [[`Filtered to: ${meta.scopeLabel}`]] : []),
            [],
            ['#', 'Section', 'Group'],
            ...sections.map((s, i) => [i + 1, s.label, s.group]),
        ],
    };
    return [cover, ...sections.map((s, i) => ({ name: `${i + 1}. ${s.label}`, aoa: blockToAoa(s.block) }))];
}

export function generateXls(title, sections, meta = {}) {
    return buildWorkbook(buildWorkbookModel(title, sections, meta));
}

// ==========================================================================
//  PowerPoint  (.pptx)
// ==========================================================================
// A real OpenXML .pptx, not an HTML page with slide-shaped divs. pptxgenjs is
// imported dynamically so its OOXML machinery only loads for the person who
// actually asks for a PowerPoint, and never lands in the app bundle.
const PPT = {
    ink: '0F172A', muted: '64748B', faint: '94A3B8', line: 'E2E8F0',
    teal: '00BFA5', red: 'FF5252', track: 'EEF2F6',
};

// 16:9 deck, 10 x 5.625in. Content sits inside these bounds on every slide.
const SLIDE = { w: 10, h: 5.625, x: 0.6, top: 1.25, bodyW: 8.8, bodyH: 3.9 };

function pptBlock(slide, block) {
    const { x, top, bodyW: w, bodyH: h } = SLIDE;

    if (block.kind === 'gauge') {
        // Coloured by the score's own band, matching the app - it was fixed red,
        // so a Low score wore the Critical colour.
        const hex = BAND_HEX[block.band] || PPT.teal;
        slide.addShape('rect', { x, y: top + 0.35, w: 0.06, h: 1.5, fill: { color: hex }, line: { color: hex } });
        slide.addText(String(block.value), { x: x + 0.2, y: top + 0.3, w: 2.6, h: 1.6, fontSize: 88, bold: true, color: hex });
        slide.addShape('roundRect', { x: x + 2.9, y: top + 0.6, w: 1.25, h: 0.38, fill: { color: hex }, line: { color: hex }, rectRadius: 0.19 });
        slide.addText(block.band || '', { x: x + 2.9, y: top + 0.6, w: 1.25, h: 0.38, fontSize: 12, bold: true, color: 'FFFFFF', align: 'center', valign: 'middle' });
        slide.addText(block.sub || '', { x: x + 2.9, y: top + 1.1, w: w - 2.9, h: 0.7, fontSize: 12, color: PPT.muted });
        return;
    }

    if (block.kind === 'metrics') {
        const items = block.items || [];
        const cols = 2;
        const cw = w / cols - 0.15;
        const rows = Math.ceil(items.length / cols) || 1;
        const ch = Math.min(1.2, h / rows - 0.12);
        items.forEach((m, i) => {
            const cx = x + (i % cols) * (cw + 0.15);
            const cy = top + Math.floor(i / cols) * (ch + 0.12);
            slide.addShape('roundRect', { x: cx, y: cy, w: cw, h: ch, fill: { color: 'FFFFFF' }, line: { color: PPT.line, width: 1 }, rectRadius: 0.06 });
            slide.addText(String(m.value == null ? '' : m.value), { x: cx + 0.15, y: cy + 0.08, w: cw - 0.3, h: 0.5, fontSize: 26, bold: true, color: PPT.ink });
            slide.addText(String(m.label == null ? '' : m.label), { x: cx + 0.15, y: cy + 0.58, w: cw - 0.3, h: 0.3, fontSize: 11, bold: true, color: PPT.ink });
            if (m.sub) slide.addText(String(m.sub), { x: cx + 0.15, y: cy + 0.85, w: cw - 0.3, h: 0.26, fontSize: 9, color: PPT.faint });
        });
        return;
    }

    if (block.kind === 'bars') {
        const items = (block.items || []).slice(0, 10);
        const max = Math.max(1, ...items.map((b) => (b.max != null ? b.max : Number(b.value) || 0)));
        const rowH = Math.min(0.42, h / Math.max(items.length, 1));
        const labelW = 2.2, valW = 0.8;
        const trackX = x + labelW, trackW = w - labelW - valW;
        items.forEach((b, i) => {
            const cy = top + i * rowH;
            slide.addText(String(b.label == null ? '' : b.label), { x, y: cy, w: labelW - 0.1, h: rowH, fontSize: 11, color: PPT.ink, valign: 'middle' });
            slide.addShape('roundRect', { x: trackX, y: cy + rowH / 2 - 0.07, w: trackW, h: 0.14, fill: { color: PPT.track }, line: { color: PPT.track }, rectRadius: 0.07 });
            const fw = Math.max(0.04, ((Number(b.value) || 0) / (b.max || max)) * trackW);
            slide.addShape('roundRect', { x: trackX, y: cy + rowH / 2 - 0.07, w: fw, h: 0.14, fill: { color: PPT.teal }, line: { color: PPT.teal }, rectRadius: 0.07 });
            slide.addText(String(b.value == null ? '' : b.value), { x: x + w - valW, y: cy, w: valW, h: rowH, fontSize: 11, bold: true, color: PPT.ink, align: 'right', valign: 'middle' });
        });
        return;
    }

    if (block.kind === 'area') {
        // A native PowerPoint chart, so it stays editable in the deck rather
        // than being a picture of a chart.
        const items = block.items || [];
        const vals = items.map((b) => Number(b.value) || 0);
        const { lo, hi } = niceScale(vals.length ? vals : [0, 1]);
        slide.addChart('area', [{
            name: block.unit || 'Value',
            labels: items.map((b) => String(b.label == null ? '' : b.label)),
            values: vals,
        }], {
            x, y: top, w, h,
            // NB: a chart's `fill` is a bare hex string here, not the { color }
            // object shapes take - passing the object put an object where a colour
            // string was expected and blew up deck generation. The plot area is
            // white by default, so it's simply omitted.
            chartColors: [PPT.teal],
            // Axis bounds follow the data. On a 0-100 default a score moving
            // between 31 and 40 sat as a flat line along the bottom edge.
            valAxisMinVal: lo, valAxisMaxVal: hi,
            showLegend: false, showValue: false,
            catAxisLabelFontSize: 9, valAxisLabelFontSize: 9,
            catAxisLabelColor: PPT.muted, valAxisLabelColor: PPT.muted,
            valGridLine: { style: 'solid', color: PPT.line, size: 1 },
            catGridLine: { style: 'none' },
        });
        return;
    }

    if (block.kind === 'table') {
        const cols = block.columns || [];
        const rows = (block.rows || []).slice(0, 12);
        const head = cols.map((c) => ({
            text: String(c), options: { bold: true, color: PPT.muted, fontSize: 9, fill: { color: 'F8FAFC' } },
        }));
        const body = rows.map((r) => r.map((c) => ({ text: String(c == null ? '' : c), options: { color: '334155', fontSize: 10 } })));
        slide.addTable([head, ...body], {
            x, y: top, w,
            colW: Array(Math.max(cols.length, 1)).fill(w / Math.max(cols.length, 1)),
            border: { type: 'solid', color: PPT.line, pt: 0.5 }, autoPage: false,
        });
        if (block.total > rows.length) {
            slide.addText(`Showing ${rows.length} of ${block.total} rows`, { x, y: SLIDE.h - 0.6, w, h: 0.25, fontSize: 9, color: PPT.faint });
        }
        return;
    }

    // narrative
    const paras = [];
    (block.paragraphs || []).forEach((p) => {
        if (p.h) paras.push({ text: p.h, options: { bold: true, fontSize: 14, color: PPT.ink, breakLine: true, paraSpaceBefore: 6 } });
        paras.push({ text: p.body || '', options: { fontSize: 11, color: '334155', breakLine: true } });
        if (p.action) paras.push({ text: `» ${p.action}`, options: { fontSize: 11, italic: true, color: '00897B', breakLine: true } });
    });
    if (paras.length) slide.addText(paras, { x, y: top, w, h, valign: 'top' });
}

export async function generatePptx(title, sections, meta = {}) {
    const { default: PptxGenJS } = await import('pptxgenjs');
    const pptx = new PptxGenJS();
    pptx.layout = 'LAYOUT_16x9';
    pptx.title = title || 'Untitled report';
    pptx.company = brandOf(meta);

    const cover = pptx.addSlide();
    cover.background = { color: 'FFFFFF' };
    cover.addShape('rect', { x: 4.65, y: 1.85, w: 0.7, h: 0.06, fill: { color: PPT.teal }, line: { color: PPT.teal } });
    cover.addText(title || 'Untitled report', { x: 0.6, y: 2.1, w: 8.8, h: 1.0, fontSize: 40, bold: true, color: PPT.ink, align: 'center' });
    cover.addText(`${brandOf(meta)} · ${todayLabel()}${meta.periodLabel ? ` · ${meta.periodLabel}` : ''}`,
        { x: 0.6, y: 3.0, w: 8.8, h: 0.5, fontSize: 14, color: PPT.muted, align: 'center' });
    if (meta.scopeLabel) {
        cover.addText(`Filtered to ${meta.scopeLabel}`,
            { x: 0.6, y: 3.45, w: 8.8, h: 0.4, fontSize: 11, color: PPT.faint, align: 'center' });
    }

    sections.forEach((s, i) => {
        const slide = pptx.addSlide();
        slide.background = { color: 'FFFFFF' };
        slide.addShape('rect', { x: SLIDE.x, y: 0.32, w: 0.35, h: 0.05, fill: { color: PPT.teal }, line: { color: PPT.teal } });
        slide.addText(`${String(s.group).toUpperCase()}${s.withDrill ? ' · WITH DRILL-DOWN' : ''}`,
            { x: SLIDE.x, y: 0.42, w: SLIDE.bodyW, h: 0.25, fontSize: 9, color: PPT.faint, charSpacing: 1 });
        slide.addText(s.label, { x: SLIDE.x, y: 0.68, w: SLIDE.bodyW, h: 0.55, fontSize: 22, bold: true, color: PPT.ink });
        pptBlock(slide, s.block);
        slide.addText(String(i + 1), { x: SLIDE.w - 0.8, y: SLIDE.h - 0.5, w: 0.4, h: 0.25, fontSize: 9, color: PPT.faint, align: 'right' });
    });

    return pptx.write({ outputType: 'blob' });
}

// ==========================================================================
//  PDF  (jsPDF, real data drawn block by block)
// ==========================================================================
export function generatePdf(title, sections, meta = {}) {
    const doc = new jsPDF({ unit: 'pt', format: 'a4' });
    const margin = 48;
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const usable = pageWidth - margin * 2;
    let y = margin;

    const ensure = (needed) => { if (y > pageHeight - margin - needed) { doc.addPage(); y = margin; } };
    const setText = (size, style, shade) => { doc.setFont('helvetica', style); doc.setFontSize(size); doc.setTextColor(shade); };

    // Masthead: a brand rule above the title gives the page a deliberate top
    // edge instead of text starting in mid-air. y is the TEXT BASELINE for the
    // title that follows, so the rule sits here and the baseline is pushed down
    // past the title's cap height - drawing it at y-12 struck through the text.
    doc.setFillColor(...DOC.teal);
    doc.rect(margin, y, 34, 3, 'F');
    y += 30;
    setText(22, 'bold', 20);
    doc.setTextColor(...DOC.ink);
    const titleLines = doc.splitTextToSize(title || 'Untitled report', usable);
    doc.text(titleLines, margin, y);
    y += 20 + (titleLines.length - 1) * 24;
    setText(9, 'normal', 130);
    doc.setTextColor(...DOC.muted);
    const headerBits = [brandOf(meta), todayLabel(), meta.periodLabel || null, `${sections.length} section${sections.length === 1 ? '' : 's'}`];
    doc.text(headerBits.filter(Boolean).join(' · '), margin, y);
    y += 10;
    // A filtered report must say what it was filtered to, or the numbers read as
    // whole-organisation figures.
    if (meta.scopeLabel) {
        const filterLines = doc.splitTextToSize(`Filtered to ${meta.scopeLabel}`, usable);
        doc.text(filterLines, margin, y + 2);
        y += 2 + filterLines.length * 10;
    }
    doc.setDrawColor(...DOC.rule); doc.setLineWidth(1); doc.line(margin, y, pageWidth - margin, y);
    y += 26;

    sections.forEach((s, i) => {
        // Reserve the heading AND the block it introduces, so a widget that
        // won't fit moves to the next page whole rather than being started at
        // the bottom and drawn off it. A block taller than a whole page can't
        // be kept together, so it just starts on a fresh one and flows.
        const HEADING_H = 29;
        const wanted = HEADING_H + estimateBlockHeight(doc, s.block, usable);
        ensure(Math.min(wanted, pageHeight - margin * 2));
        setText(8, 'bold', 150);
        doc.setTextColor(...DOC.teal);
        doc.text(`SECTION ${i + 1} · ${String(s.group).toUpperCase()}${s.withDrill ? ' · WITH DRILL-DOWN' : ''}`, margin, y);
        y += 13;
        setText(13, 'bold', 20);
        doc.setTextColor(...DOC.ink);
        doc.text(s.label, margin, y);
        y += 6;
        doc.setDrawColor(...DOC.hairline); doc.setLineWidth(0.5);
        doc.line(margin, y, margin + usable, y);
        y += 12;
        drawBlock(doc, s.block, { margin, usable, pageHeight, ensure, setText, getY: () => y, setY: (v) => { y = v; } });
        y += 18;
    });

    const pageCount = doc.internal.getNumberOfPages();
    for (let p = 1; p <= pageCount; p++) {
        doc.setPage(p);
        doc.setDrawColor(...DOC.rule); doc.setLineWidth(0.5);
        doc.line(margin, pageHeight - 32, pageWidth - margin, pageHeight - 32);
        setText(8, 'normal', 150);
        doc.setTextColor(...DOC.faint);
        doc.text(`Page ${p} of ${pageCount}`, pageWidth - margin, pageHeight - 20, { align: 'right' });
        doc.text(`${brandOf(meta)} · Confidential`, margin, pageHeight - 20);
    }
    return doc.output('blob');
}

// Truncate the given text to fit maxWidth (pt) in the doc's current font, adding
// an ellipsis so clipped table cells / bar labels read as intentional, not cut off.
function clipText(doc, text, maxWidth) {
    const s = String(text ?? '');
    if (maxWidth <= 0 || doc.getTextWidth(s) <= maxWidth) return s;
    let lo = 0, hi = s.length;
    while (lo < hi) {
        const mid = (lo + hi + 1) >> 1;
        if (doc.getTextWidth(s.slice(0, mid) + '\u2026') <= maxWidth) lo = mid; else hi = mid - 1;
    }
    return (s.slice(0, lo).replace(/\s+$/, '') || s.slice(0, 1)) + '\u2026';
}

// Approximate drawn height of a block, so a widget can be kept whole on one
// page. Mirrors the row/step sizes drawBlock uses; being a few points out only
// affects when a page break is taken, never what is drawn.
function estimateBlockHeight(doc, block, usable) {
    if (!block) return 0;
    if (block.kind === 'gauge')   return 62;
    if (block.kind === 'metrics') return Math.ceil((block.items?.length || 0) / 2) * 58;
    if (block.kind === 'bars')    return (block.items?.length || 0) * 17 + 4;
    if (block.kind === 'area')    return 124;
    if (block.kind === 'table') {
        const rows = Math.min(block.rows?.length || 0, DOC_TABLE_LIMIT);
        return 26 + Math.max(rows, 1) * 14 + (block.total > rows ? 12 : 0);
    }
    if (block.kind === 'narrative') {
        return (block.paragraphs || []).reduce((h, p) => {
            let n = p.h ? 15 : 0;
            n += doc.splitTextToSize(p.body || '', usable).length * 12 + 2;
            if (p.action) n += doc.splitTextToSize(`» ${p.action}`, usable).length * 12 + 2;
            return h + n;
        }, 0);
    }
    return 60;
}

function drawBlock(doc, block, ctx) {
    const { margin, usable, ensure, setText } = ctx;
    let y = ctx.getY();

    // ensure() page-breaks against the CALLER's y and resets it to the top
    // margin. This function tracks its own y as it draws, so calling ensure()
    // directly compared the wrong position and, worse, left the local y pointing
    // at the old bottom-of-page after a break - which is how blocks ended up
    // drawn off the bottom edge. need() syncs in both directions.
    const need = (h) => { ctx.setY(y); ensure(h); y = ctx.getY(); };

    if (block.kind === 'gauge') {
        need(76);
        // Score coloured by its own band - it was hardcoded red, so a Low score
        // was presented in the colour the product uses for Critical.
        const rgb = bandRgb(block.band);
        doc.setFillColor(...rgb);
        doc.rect(margin, y + 2, 3, 44, 'F');
        doc.setFont('helvetica', 'bold'); doc.setFontSize(44);
        doc.setTextColor(...rgb);
        doc.text(String(block.value), margin + 12, y + 36);
        const numW = doc.getTextWidth(String(block.value));
        const bx = margin + 12 + numW + 14;
        // Band pill, same shape the app uses next to a score.
        setText(9, 'bold', 30);
        const bw = doc.getTextWidth(block.band || '') + 16;
        doc.setFillColor(...rgb);
        doc.roundedRect(bx, y + 10, bw, 16, 8, 8, 'F');
        doc.setTextColor(255, 255, 255);
        doc.text(block.band || '', bx + 8, y + 21);
        setText(9, 'normal', 120);
        doc.setTextColor(...DOC.muted);
        doc.text(doc.splitTextToSize(block.sub || '', usable - (bx - margin)), bx, y + 38);
        y += 62;
    } else if (block.kind === 'metrics') {
        const cols = 2;
        const gap = 10;
        const cellW = (usable - gap) / cols;
        const cellH = 52;
        const rows = Math.ceil(block.items.length / cols);
        need(rows * (cellH + 6));
        block.items.forEach((m, i) => {
            const cx = margin + (i % cols) * (cellW + gap);
            const cy = y + Math.floor(i / cols) * (cellH + 6);
            // Each figure sits in its own card so a strip of four reads as four
            // things, not one run-on paragraph of numbers.
            doc.setFillColor(...DOC.zebra);
            doc.setDrawColor(...DOC.rule); doc.setLineWidth(0.5);
            doc.roundedRect(cx, cy, cellW, cellH, 4, 4, 'FD');
            setText(18, 'bold', 20); doc.setTextColor(...DOC.ink);
            doc.text(clipText(doc, m.value, cellW - 20), cx + 10, cy + 24);
            setText(9, 'bold', 90); doc.setTextColor(...DOC.body);
            doc.text(clipText(doc, m.label, cellW - 20), cx + 10, cy + 37);
            setText(8, 'normal', 140); doc.setTextColor(...DOC.faint);
            doc.text(clipText(doc, m.sub || '', cellW - 20), cx + 10, cy + 47);
        });
        y += rows * (cellH + 6);
    } else if (block.kind === 'bars') {
        const max = Math.max(1, ...block.items.map((b) => (b.max != null ? b.max : Number(b.value) || 0)));
        need(block.items.length * 19 + 4);
        const labelW = 130, valW = 52, trackX = margin + labelW, trackW = usable - labelW - valW;
        block.items.forEach((b) => {
            setText(9, 'normal', 60); doc.setTextColor(...DOC.body);
            doc.text(clipText(doc, b.label, labelW - 8), margin, y + 9);
            // Rounded track + bar, so the chart matches the on-screen widgets
            // rather than looking like a raw rectangle plot.
            doc.setFillColor(...DOC.hairline);
            doc.roundedRect(trackX, y + 2, trackW, 9, 4.5, 4.5, 'F');
            doc.setFillColor(...DOC.teal);
            const w = Math.max(4, Math.round((Number(b.value) || 0) / (b.max || max) * trackW));
            doc.roundedRect(trackX, y + 2, w, 9, 4.5, 4.5, 'F');
            setText(9, 'bold', 40); doc.setTextColor(...DOC.ink);
            doc.text(String(b.value), margin + usable, y + 10, { align: 'right' });
            y += 17;
        });
    } else if (block.kind === 'area') {
        const items = block.items || [];
        const chartH = 110, labelH = 14;
        need(chartH + labelH + 6);
        // Scale to the data, not to an arbitrary 0-100: a score that moves
        // between 31 and 40 was drawn as a flat line along the floor with the
        // top two thirds of the plot empty. block.max is still honoured as a
        // ceiling hint where a resolver sets one.
        const values = items.map((b) => Number(b.value) || 0);
        const { lo, hi, ticks } = niceScale(values.length ? values : [0, 1]);
        const axisW = 26;
        const plotX = margin + axisW;
        const plotW = usable - axisW;
        const n = Math.max(1, items.length - 1);
        const px = (i) => plotX + (i / n) * plotW;
        const py = (v) => y + chartH - (((Number(v) || 0) - lo) / (hi - lo || 1)) * chartH;
        // Gridlines + value labels, so a reader can tell what the line is worth.
        setText(7.5, 'normal', 150);
        ticks.forEach((t) => {
            const ty = py(t);
            doc.setDrawColor(...DOC.hairline); doc.setLineWidth(0.5);
            doc.line(plotX, ty, plotX + plotW, ty);
            doc.setTextColor(...DOC.faint);
            doc.text(String(t), plotX - 6, ty + 2.5, { align: 'right' });
        });
        // Filled silhouette: one slim vertical bar per point, then the line on top.
        doc.setFillColor(...DOC.tealSoft);
        items.forEach((b, i) => {
            const x0 = i === 0 ? px(0) : (px(i - 1) + px(i)) / 2;
            const x1 = i === items.length - 1 ? px(i) : (px(i) + px(i + 1)) / 2;
            const top = py(b.value);
            doc.rect(x0, top, Math.max(0.5, x1 - x0), y + chartH - top, 'F');
        });
        doc.setDrawColor(...DOC.teal); doc.setLineWidth(1.5);
        for (let i = 1; i < items.length; i++) doc.line(px(i - 1), py(items[i - 1].value), px(i), py(items[i].value));
        doc.setDrawColor(...DOC.rule); doc.setLineWidth(0.5);
        doc.line(plotX, y + chartH, plotX + plotW, y + chartH);
        setText(8, 'normal', 120);
        doc.setTextColor(...DOC.muted);
        if (items.length) {
            doc.text(String(items[0].label ?? ''), plotX, y + chartH + 11);
            doc.text(String(items[items.length - 1].label ?? ''), plotX + plotW, y + chartH + 11, { align: 'right' });
        }
        y += chartH + labelH;
    } else if (block.kind === 'table') {
        const rows = block.rows.slice(0, DOC_TABLE_LIMIT);
        const ncol = block.columns.length;
        const colW = usable / ncol;
        need(26);
        // Header band + zebra rows: at 20 rows across a full page width, plain
        // text columns are hard to track across; a tinted header and alternating
        // row fill make a line scannable.
        const rowH = 14;
        const drawHead = () => {
            doc.setFillColor(...DOC.zebra);
            doc.rect(margin, y - 9, usable, 15, 'F');
            setText(8, 'bold', 110);
            doc.setTextColor(...DOC.muted);
            block.columns.forEach((c, i) => doc.text(clipText(doc, String(c), colW - 8), margin + i * colW + 4, y));
            y += 6;
            doc.setDrawColor(...DOC.rule); doc.setLineWidth(0.5);
            doc.line(margin, y, margin + usable, y);
            y += 11;
        };
        drawHead();
        setText(8.5, 'normal', 60);
        if (rows.length === 0) { setText(8.5, 'italic', 150); doc.text('(no rows)', margin + 4, y); y += 13; setText(8.5, 'normal', 60); }
        rows.forEach((r, ri) => {
            const before = y;
            need(rowH + 2);
            // A table that spans a page break gets its header repeated, so the
            // continuation isn't a headless block of numbers.
            if (y !== before) { drawHead(); setText(8.5, 'normal', 60); }
            if (ri % 2 === 1) {
                doc.setFillColor(...DOC.zebra);
                doc.rect(margin, y - 9, usable, rowH, 'F');
            }
            setText(8.5, 'normal', 60);
            doc.setTextColor(...DOC.body);
            r.forEach((cell, i) => {
                doc.text(clipText(doc, cell, colW - 8), margin + i * colW + 4, y);
            });
            y += rowH;
        });
        doc.setDrawColor(...DOC.rule); doc.setLineWidth(0.5);
        doc.line(margin, y - 8, margin + usable, y - 8);
        if (block.total > rows.length) {
            setText(8, 'italic', 150);
            doc.setTextColor(...DOC.faint);
            doc.text(`Showing ${rows.length} of ${block.total} rows · full table in Large data tables`, margin + 4, y + 4);
            y += 12;
        }
    } else if (block.kind === 'narrative') {
        block.paragraphs.forEach((p) => {
            if (p.h) { need(20); setText(11, 'bold', 25); doc.setTextColor(...DOC.ink); doc.text(doc.splitTextToSize(p.h, usable), margin, y); y += 15; }
            setText(9.5, 'normal', 70);
            doc.setTextColor(...DOC.body);
            const lines = doc.splitTextToSize(p.body, usable);
            need(lines.length * 12 + 4);
            doc.text(lines, margin, y); y += lines.length * 12 + 2;
            if (p.action) { setText(9, 'italic', 0); doc.setTextColor(0, 137, 123); const al = doc.splitTextToSize(`\u00BB ${p.action}`, usable); need(al.length * 12); doc.text(al, margin, y); y += al.length * 12 + 2; }
        });
    }
    ctx.setY(y);
}

// ==========================================================================
//  Document dispatch
// ==========================================================================
const FORMAT_EXT = { PDF: 'pdf', PowerPoint: 'pptx', Excel: 'xlsx', CSV: 'zip' };

// Single source of truth for a composed-document filename so the download and
// any user-facing label (e.g. the email-queued toast) can never drift from the
// real extension (PowerPoint -> .html, Excel -> .xlsx, CSV -> .zip).
export function documentFilename(format, title) {
    const base = (title || 'export').replace(/\s+/g, '-').toLowerCase().replace(/[^a-z0-9-]/g, '');
    return `${base}.${FORMAT_EXT[format] || 'pdf'}`;
}

// Async because the CSV zip assembles off the main thread; callers await.
export async function generateDocument(format, title, sections, meta = {}) {
    const filename = documentFilename(format, title);
    if (format === 'CSV')        return { content: await generateCsvZip(title, sections, meta), mime: 'application/zip',  filename };
    if (format === 'Excel')      return { content: generateXls(title, sections, meta),  mime: XLSX_MIME,                  filename };
    if (format === 'PowerPoint') return { content: await generatePptx(title, sections, meta), mime: PPTX_MIME, filename };
    return { content: generatePdf(title, sections, meta), mime: 'application/pdf', filename };
}

// ==========================================================================
//  Large-table exporters  (CSV / Excel / JSON / PDF at full row count)
// ==========================================================================
function tableCsv(columns, rows) {
    return [columns.map(escapeCsv).join(','), ...rows.map((r) => r.map(escapeCsv).join(','))].join('\n');
}

function tableXls(label, columns, rows) {
    return buildWorkbook([{ name: label, aoa: [columns, ...rows] }]);
}

function tableJson(columns, rows) {
    const keys = columns.map((c) => c.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, ''));
    return JSON.stringify(rows.map((r) => Object.fromEntries(keys.map((k, i) => [k, r[i]]))), null, 2);
}

function tablePdf(label, columns, rows, meta = {}) {
    const doc = new jsPDF({ unit: 'pt', format: 'a4', orientation: 'landscape' });
    const margin = 36;
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const usable = pageWidth - margin * 2;
    const colW = usable / columns.length;
    let y = margin;
    doc.setFont('helvetica', 'bold'); doc.setFontSize(15); doc.setTextColor(20);
    doc.text(label, margin, y); y += 16;
    doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor(130);
    doc.text(`${rows.length} rows · ${todayLabel()} · ${brandOf(meta)}`, margin, y); y += 16;

    const header = () => {
        doc.setFont('helvetica', 'bold'); doc.setFontSize(8); doc.setTextColor(110);
        columns.forEach((c, i) => doc.text(String(c), margin + i * colW, y));
        y += 4; doc.setDrawColor(200); doc.setLineWidth(0.5); doc.line(margin, y, margin + usable, y); y += 10;
        doc.setFont('helvetica', 'normal'); doc.setFontSize(8); doc.setTextColor(60);
    };
    header();
    if (rows.length === 0) { doc.setFont('helvetica', 'italic'); doc.setTextColor(150); doc.text('(no rows)', margin, y); doc.setFont('helvetica', 'normal'); doc.setTextColor(60); y += 12; }
    rows.forEach((r) => {
        if (y > pageHeight - margin - 14) { doc.addPage(); y = margin; header(); }
        r.forEach((cell, i) => { doc.text(clipText(doc, cell, colW - 6), margin + i * colW, y); });
        y += 12; doc.setDrawColor(238); doc.line(margin, y - 4, margin + usable, y - 4);
    });
    const pageCount = doc.internal.getNumberOfPages();
    doc.setFontSize(8); doc.setTextColor(150);
    for (let p = 1; p <= pageCount; p++) { doc.setPage(p); doc.text(`Page ${p} of ${pageCount}`, pageWidth - margin, pageHeight - 16, { align: 'right' }); }
    return doc.output('blob');
}

export function generateTable(format, label, columns, rows, meta = {}) {
    const base = (label || 'table').replace(/\s+/g, '-').toLowerCase().replace(/[^a-z0-9-]/g, '');
    if (format === 'CSV')   return { content: tableCsv(columns, rows),        mime: 'text/csv;charset=utf-8',   filename: `${base}.csv` };
    if (format === 'Excel') return { content: tableXls(label, columns, rows), mime: XLSX_MIME,                  filename: `${base}.xlsx` };
    if (format === 'JSON')  return { content: tableJson(columns, rows),       mime: 'application/json',          filename: `${base}.json` };
    return { content: tablePdf(label, columns, rows, meta), mime: 'application/pdf', filename: `${base}.pdf` };
}
